class PredictiveSearch extends HTMLElement {
    constructor() {
        super();

        this.input = this.querySelector('input[type="search"]');
        this.predictiveSearchResults = this.querySelector('#predictive-search-results');
        this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
        this.isOpen = false;
        this.abortController = null;
        this.cachedResults = {};

        this.setupEventListeners();
    }

    setupEventListeners() {
        this.input.addEventListener('input', this.debounce((event) => {
            this.onChange(event);
        }, 300).bind(this));

        this.input.addEventListener('focus', this.onFocus.bind(this));

        this.addEventListener('focusout', this.onFocusOut.bind(this));
        this.addEventListener('keydown', this.onKeydown.bind(this));

        // Click outside to close
        document.addEventListener('click', (event) => {
            if (!this.contains(event.target)) {
                this.close();
            }
        });
    }

    onFocus() {
        const searchTerm = this.input.value.trim();
        if (searchTerm.length) {
            if (this.cachedResults[searchTerm]) {
                this.renderSearchResults(this.cachedResults[searchTerm]);
            } else {
                this.onChange();
            }
        }
    }

    onFocusOut() {
        setTimeout(() => {
            if (!this.contains(document.activeElement)) {
                this.close();
            }
        }, 10);
    }

    onKeydown(event) {
        if (event.code === 'Escape') {
            this.close();
            this.input.blur();
            return;
        }

        if (event.code === 'ArrowDown' || event.code === 'ArrowUp') {
            event.preventDefault();
            this.navigateResults(event.code === 'ArrowDown' ? 1 : -1);
        }

        if (event.code === 'Enter') {
            const selectedItem = this.querySelector('[aria-selected="true"] a');
            if (selectedItem) {
                event.preventDefault();
                selectedItem.click();
            }
        }
    }

    navigateResults(direction) {
        if (!this.isOpen) return;

        const items = Array.from(this.querySelectorAll('.predictive-search__list-item, .predictive-search__view-all-button'));
        if (!items.length) return;

        let currentIndex = items.findIndex(item => item.getAttribute('aria-selected') === 'true' || item.classList.contains('focused'));

        if (currentIndex !== -1) {
            items[currentIndex].setAttribute('aria-selected', 'false');
            items[currentIndex].classList.remove('focused');
        }

        let nextIndex = currentIndex + direction;
        if (nextIndex >= items.length) nextIndex = 0;
        if (nextIndex < 0) nextIndex = items.length - 1;

        const nextItem = items[nextIndex];
        nextItem.setAttribute('aria-selected', 'true');
        nextItem.classList.add('focused');

        // Ensure visible
        nextItem.scrollIntoView({ block: 'nearest' });
    }

    onChange() {
        const searchTerm = this.input.value.trim();

        if (!searchTerm.length) {
            this.close();
            return;
        }

        if (this.cachedResults[searchTerm]) {
            this.renderSearchResults(this.cachedResults[searchTerm]);
            return;
        }

        this.getSearchResults(searchTerm);
    }

    getSearchResults(searchTerm) {
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        const url = `/search/suggest?q=${searchTerm}&resources[type]=product,collection,page&resources[options][unavailable_products]=last&section_id=predictive-search`;

        fetch(url, { signal: this.abortController.signal })
            .then((response) => {
                if (!response.ok) {
                    this.close();
                    return;
                }
                return response.text();
            })
            .then((text) => {
                const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
                this.cachedResults[searchTerm] = resultsMarkup;
                this.renderSearchResults(resultsMarkup);
            })
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    this.close();
                }
            });
    }

    renderSearchResults(resultsMarkup) {
        this.predictiveSearchResults.innerHTML = resultsMarkup;

        // Wire up the "View All" button to submit the form
        const viewAllBtn = this.querySelector('.predictive-search__view-all-button');
        if (viewAllBtn) {
            viewAllBtn.addEventListener('click', () => {
                this.querySelector('form').submit();
            });
        }

        this.open();
    }

    open() {
        this.predictiveSearchResults.style.display = 'block';
        this.input.setAttribute('aria-expanded', 'true');
        this.isOpen = true;
    }

    close() {
        this.predictiveSearchResults.style.display = 'none';
        this.input.setAttribute('aria-expanded', 'false');
        this.isOpen = false;
    }

    debounce(fn, wait) {
        let t;
        return (...args) => {
            clearTimeout(t);
            t = setTimeout(() => fn.apply(this, args), wait);
        };
    }
}

if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch);
}
